import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { FormGroup, FormControl, Validators, NonNullableFormBuilder, FormArray, AbstractControl } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { EventViewModel } from '../../../models-views/event.view.model';
import { AppCommon } from '../../../providers/app-common';
import { DatePipe } from '@angular/common';
import { ClientPptGenService } from '../../../services/ppt-gen-services/ppt-gen-client.ppt.service';

interface IModalData {
  eventData: any;
  selectedItems: any;
}
interface EventDetailsFormValues {
  eventName: string;
  eventStartDate: string | null;
  includeCoverPage: boolean;
  includeThankYouPage: boolean;
}

interface AnswerFormValues {
  selected: boolean;
  questionText: string;
  answerText: string;
  originalQuestionId: number | null;
  originalAnswerId: number | null;
}

interface PhotoFormValues {
  selected: boolean;
  fileUrl: string; // This will now store the full URL
  fileName: string;
  base64Content: string | null; // For custom uploads or if fetched as base64
  originalFileReferenceId: number | null; // To identify original submission photos
}

interface VendorSubmissionFormValues {
  vendorName: string;
  vendorId: number | null;
  sectionHeading: string; // Added for section heading
  answers: AnswerFormValues[];
  photos: PhotoFormValues[];
}

type AnswerFormGroup = FormGroup<{
  selected: FormControl<boolean>;
  questionText: FormControl<string>;
  answerText: FormControl<string>;
  originalQuestionId: FormControl<number | null>;
  originalAnswerId: FormControl<number | null>;
}>;

type PhotoFormGroup = FormGroup<{
  selected: FormControl<boolean>;
  fileUrl: FormControl<string>;
  fileName: FormControl<string>;
  base64Content: FormControl<string | null>;
  originalFileReferenceId: FormControl<number | null>;
}>;

type VendorFormGroup = FormGroup<{
  vendorName: FormControl<string>;
  vendorId: FormControl<number | null>;
  sectionHeading: FormControl<string>; // Added for section heading
  answers: FormArray<AnswerFormGroup>;
  photos: FormArray<PhotoFormGroup>;
}>;

@Component({
  selector: 'app-genarate-client-ppt',
  standalone: false,
  templateUrl: './genarate-client-ppt.component.html',
  styleUrl: './genarate-client-ppt.component.scss',
  providers: [DatePipe]
})
export class GenarateClientPptComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly nzModalData: IModalData = inject(NZ_MODAL_DATA);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly message = inject(NzMessageService);

  stepIndex = 0;
  eventData: any;
  selectedItems: any;
  isLoading = false;
  loginModel: any;
  private pendingApiCallsCount: { [key: number]: number } = {};
  dataList: any[] = [];

  validateForm: FormGroup<{ [K in keyof EventDetailsFormValues]: FormControl<EventDetailsFormValues[K]> }> = this.fb.group({
    eventName: ['', [Validators.required]],
    eventStartDate: [null as string | null, [Validators.required]],
    includeCoverPage: [true],
    includeThankYouPage: [true]
  });

  vendorSubmissionsForm: FormGroup<{ vendors: FormArray<VendorFormGroup> }> = this.fb.group({
    vendors: this.fb.array<VendorFormGroup>([])
  });

  reviewData: {
    eventDetails?: EventDetailsFormValues;
    vendorSubmissions?: { vendors: VendorSubmissionFormValues[] };
  } = {};

  constructor(
    private modalRef: NzModalRef,
    private vm: EventViewModel,
    private el: ElementRef,
    private datePipe: DatePipe,
    private clientPptGenService: ClientPptGenService
  ) {
    this.loginModel = AppCommon.getloggedInObject();
  }

  ngOnInit(): void {
    this.selectedItems = JSON.parse(JSON.stringify(this.nzModalData.selectedItems));
    this.eventData = this.nzModalData.eventData;

    if (this.eventData) {
      this.validateForm.patchValue({
        eventName: this.eventData.name || '',
        eventStartDate: this.eventData.event_datetime ? String(this.datePipe.transform(this.eventData.event_datetime, 'EEEE, MMMM d, y, h:mm a')) : null,
        includeCoverPage: true,
        includeThankYouPage: true,
      });
    }

    this.isLoading = true;
    this.vm.parentGenarateClientPptComponent = this;
    this.initData();
  }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void { }

  initData(): void {
    if (this.selectedItems.length === 0) {
      this.allApiCallsFinished();
      return;
    }

    this.selectedItems.forEach((item, index) => {
      const formId = item.quotation_request_vendor_details.quotation_request_details.outsourcing_form;
      const submissionId = item.id;
      const trackingId = index;
      const quotation_request_vendor = item.quotation_request_vendor;

      const newItem = {
        form_id: formId,
        submission_id: submissionId,
        quotation_request_vendor: quotation_request_vendor,
        isFinalized: item.quotation_request_vendor_details.is_finalized,
        isShortlisted: item.quotation_request_vendor_details.is_shortlisted,
        answers: [],
        data_received: {
          answers: false
        },
        tracking_id: trackingId,
        photos: [] // Initialize photos array for each item
      };
      this.dataList.push(newItem);
      this.pendingApiCallsCount[trackingId] = 0;
      this.pendingApiCallsCount[trackingId]++;
      this.vm.getVendorAnswars(1, 100, submissionId, 'genarate-client-ppt');
    });
  }

  getHttpsUrl(url: string){
    if (!url) {
      return null;
    }

    // console.log("Original URL:", url);

    if (!url.includes('https') && !url.includes('localhost')) {
      return url.replace('http', 'https');
    }
    return url;
  }

  vendorAnswersRecieved(response: any, submission_id: number): void {
    const item = this.dataList.find(el => el.submission_id === submission_id);
    if (item) {
      const trackingId = item.tracking_id;
      const results = response.results || [];
      const extractedPhotos: any[] = [];

      results.forEach((element: any) => {
        if (element.submission_details) delete element.submission_details;

        if (element.question_details?.question_type === 'FILE_UPLOAD' && element.answer_file_references) {
          element.answer_file_references.forEach((fileRef: any) => {
            // All veriables
            const fileName = fileRef.file_name;
            const fileNameLower = fileName.toLowerCase();

            const fileId = fileRef.id;
            var mediaUrl = fileRef.file_url;
            if (fileNameLower.includes('.jpg') || fileNameLower.includes('.png') || fileNameLower.includes('.jpeg')) { 
              mediaUrl = this.getHttpsUrl(mediaUrl);
              extractedPhotos.push({ file_url: mediaUrl, file_name: fileName, id: fileId});
            }
          });
        }
      });
      item.answers = results;
      item.photos = extractedPhotos;
      item.data_received.answers = true;
      this.checkAndCallAllFinished(trackingId);
    }
  }

  private checkAndCallAllFinished(trackingId: number): void {
    if (this.pendingApiCallsCount[trackingId] > 0) {
      this.pendingApiCallsCount[trackingId]--;
    }

    const item = this.dataList[trackingId];
    if (item && item.data_received.answers) {
      const allItemsProcessed = this.dataList.every(dataItem =>
        dataItem.data_received.answers
      );

      if (allItemsProcessed) {
        this.allApiCallsFinished();
      }
    }
  }

  private allApiCallsFinished(): void {
    // console.log('All API calls for all selected items have finished!');
    this.isLoading = false;
    // console.log('Raw dataList:', this.dataList);
    this.populateVendorSubmissionsForm();
  }

  /**
   * Creates an AnswerFormGroup, automatically deselecting if the answer is empty.
   * @param questionText The text of the question.
   * @param answerTextValue The answer text value.
   * @param originalQuestionId The original question ID.
   * @param originalAnswerId The original answer ID.
   * @returns An initialized AnswerFormGroup.
   */
  private createAnswerFormGroup(questionText: string, answerTextValue: any, originalQuestionId: number | null, originalAnswerId: number | null): AnswerFormGroup {
    let answerTextString: string;
    if (typeof answerTextValue === 'boolean') {
      answerTextString = String(answerTextValue);
    } else {
      answerTextString = String(answerTextValue || '').trim();
    }

    const isAnswerProvided = answerTextString !== '' && answerTextString !== 'null' && answerTextString !== 'undefined';

    const group = this.fb.group({
      selected: this.fb.control(isAnswerProvided),
      questionText: this.fb.control(questionText),
      answerText: this.fb.control(answerTextValue),
      originalQuestionId: new FormControl<number | null>(originalQuestionId, { nonNullable: false }),
      originalAnswerId: new FormControl<number | null>(originalAnswerId, { nonNullable: false })
    }) as AnswerFormGroup;

    if (isAnswerProvided) {
      group.get('questionText')?.addValidators(Validators.required);
      group.get('answerText')?.addValidators(Validators.required);
    }

    group.get('selected')?.valueChanges.subscribe(selected => {
      const questionTextControl = group.get('questionText');
      const answerTextControl = group.get('answerText');

      if (selected) {
        questionTextControl?.addValidators(Validators.required);
        answerTextControl?.addValidators(Validators.required);
      } else {
        questionTextControl?.removeValidators(Validators.required);
        answerTextControl?.removeValidators(Validators.required);
      }
      questionTextControl?.updateValueAndValidity();
      answerTextControl?.updateValueAndValidity();
    });

    group.get('questionText')?.updateValueAndValidity();
    group.get('answerText')?.updateValueAndValidity();
    group.get('selected')?.updateValueAndValidity();

    return group;
  }

  private createPhotoFormGroup(fileUrl: string, fileName: string, originalFileReferenceId: number | null, base64Content: string | null = null): PhotoFormGroup {
    return this.fb.group({
      selected: this.fb.control(true),
      fileUrl: this.fb.control(fileUrl),
      fileName: this.fb.control(fileName),
      base64Content: this.fb.control(base64Content),
      originalFileReferenceId: new FormControl<number | null>(originalFileReferenceId, { nonNullable: false }),
    }) as PhotoFormGroup;
  }

  private populateVendorSubmissionsForm(): void {
    const vendorsFormArray = this.vendorSubmissionsForm.get('vendors') as FormArray<VendorFormGroup>;
    vendorsFormArray.clear();

    this.dataList.forEach(item => {
      const vendorRequest = this.selectedItems.find(el => el.quotation_request_vendor === item.quotation_request_vendor);
      const vendorName = vendorRequest.quotation_request_vendor_details.vendor_details.name || `Vendor ${item.tracking_id + 1}`;

      const vendorFormGroup: VendorFormGroup = this.fb.group({
        vendorName: this.fb.control(vendorName, [Validators.required]),
        vendorId: this.fb.control(item.quotation_request_vendor.id || null),
        sectionHeading: this.fb.control(vendorName), // Initialize with vendorName
        answers: this.fb.array<AnswerFormGroup>([]),
        photos: this.fb.array<PhotoFormGroup>([])
      }) as VendorFormGroup;

      const answersFormArray = vendorFormGroup.get('answers') as FormArray<AnswerFormGroup>;
      const photosFormArray = vendorFormGroup.get('photos') as FormArray<PhotoFormGroup>;

      item.answers.forEach(answer => {
        if (!answer) {
          console.warn('Skipping null or undefined answer object in dataList for submission:', item.submission_id);
          return;
        }

        if (answer.question_details?.question_type === 'FILE_UPLOAD') {
          console.log('Skipping File field (handled in photos array):', answer.question_details.question_text);
          return;
        }

        let answerTextValue: any;
        if (answer.answer_boolean !== undefined && answer.answer_boolean !== null) {
          answerTextValue = answer.answer_boolean;
        } else if (answer.answer_date !== undefined && answer.answer_date !== null) {
          answerTextValue = answer.answer_date;
        } else if (answer.answer_number !== undefined && answer.answer_number !== null) {
          answerTextValue = answer.answer_number;
        } else if (answer.answer_selected_choice_ids !== undefined && answer.answer_selected_choice_ids !== null) {
          answerTextValue = Array.isArray(answer.answer_selected_choice_ids) ? answer.answer_selected_choice_ids.join(', ') : answer.answer_selected_choice_ids;
        } else if (answer.answer_text_long !== undefined && answer.answer_text_long !== null) {
          answerTextValue = answer.answer_text_long;
        } else if (answer.answer_text_short !== undefined && answer.answer_text_short !== null) {
          answerTextValue = answer.answer_text_short;
        } else if (answer.answer_time !== undefined && answer.answer_time !== null) {
          answerTextValue = answer.answer_time;
        } else {
          answerTextValue = '';
        }

        const questionText = (answer.question_details?.question_text ?? 'No Question Text') as string;
        const originalQuestionId = (answer.question ?? null) as number | null;
        const originalAnswerId = (answer.id ?? null) as number | null;

        answersFormArray.push(this.createAnswerFormGroup(questionText, answerTextValue, originalQuestionId, originalAnswerId));
      });

      item.photos.forEach((photo: any) => {
        photosFormArray.push(this.createPhotoFormGroup(photo.file_url, photo.file_name, photo.id));
      });

      vendorsFormArray.push(vendorFormGroup);
    });

    // console.log('Vendor Submissions Form Initialized:', this.vendorSubmissionsForm.value);
  }

  onIndexChange(index: number): void {
    if (index === 2 && this.stepIndex === 1) {
      if (!this.vendorSubmissionsForm.valid) {
        this.markFormGroupTouched(this.vendorSubmissionsForm);
        this.message.error('Please correct the errors in the Vendor Submissions form before proceeding.');
        return;
      }
      this.reviewData.vendorSubmissions = this.vendorSubmissionsForm.value as { vendors: VendorSubmissionFormValues[] };
    }
    if (index === 3 && this.stepIndex === 2) {
      this.reviewData.vendorSubmissions = this.vendorSubmissionsForm.value as { vendors: VendorSubmissionFormValues[] };
      // console.log('Review Data after photo selection:', this.reviewData.vendorSubmissions);
    }

    this.stepIndex = index;
  }

  submitForm(): void {
    if (this.stepIndex === 0) {
      if (this.validateForm.valid) {
        // console.log('Event Details Form Values:', this.validateForm.value);
        this.message.success('Event Details submitted successfully!');
        this.reviewData.eventDetails = this.validateForm.value as EventDetailsFormValues;
        this.onIndexChange(1);
      } else {
        this.markFormGroupTouched(this.validateForm);
        this.message.error('Please correct the errors in the Event Details form.');
      }
    } else if (this.stepIndex === 1) {
      if (this.vendorSubmissionsForm.valid) {
        // console.log('Vendor Submissions Form Values:', this.vendorSubmissionsForm.value);
        this.message.success('Vendor Submissions updated successfully!');
        this.reviewData.vendorSubmissions = this.vendorSubmissionsForm.value as { vendors: VendorSubmissionFormValues[] };
        this.onIndexChange(2);
      } else {
        this.markFormGroupTouched(this.vendorSubmissionsForm);
        this.message.error('Please correct the errors in the Vendor Submissions form.');
      }
    } else if (this.stepIndex === 2) {
      this.onIndexChange(3);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  hasError(controlName: keyof EventDetailsFormValues): boolean {
    const control = this.validateForm.get(controlName);
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  get vendorsFormArray(): FormArray<VendorFormGroup> {
    return this.vendorSubmissionsForm.get('vendors') as FormArray<VendorFormGroup>;
  }

  getAnswersFormArray(vendorIndex: number): FormArray<AnswerFormGroup> {
    const vendorGroup = this.vendorsFormArray.at(vendorIndex);
    return vendorGroup?.get('answers') as FormArray<AnswerFormGroup> ?? this.fb.array<AnswerFormGroup>([]);
  }

  getAnswersFormGroup(vendorIndex: number, answerIndex: number): FormGroup {
    return this.getAnswersFormArray(vendorIndex).at(answerIndex) as FormGroup;
  }

  getAnswerControl(vendorIndex: number, answerIndex: number, controlName: keyof AnswerFormValues): FormControl<any> {
    const answersArray = this.getAnswersFormArray(vendorIndex);
    if (!answersArray) {
      // console.warn(`Answers FormArray not found for vendorIndex: ${vendorIndex}`);
      return new FormControl();
    }
    const answerGroup = answersArray.at(answerIndex);
    if (!answerGroup) {
      // console.warn(`Answer FormGroup not found for vendorIndex: ${vendorIndex}, answerIndex: ${answerIndex}`);
      return new FormControl();
    }
    return answerGroup.get(controlName) as FormControl<any>;
  }

  getAnswerSelected(vendorIndex: number, answerIndex: number): boolean {
    return this.getAnswerControl(vendorIndex, answerIndex, 'selected').value;
  }

  setAnswerSelected(vendorIndex: number, answerIndex: number, value: boolean): void {
    const control = this.getAnswerControl(vendorIndex, answerIndex, 'selected');
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  getAnswerQuestionText(vendorIndex: number, answerIndex: number): string {
    return this.getAnswerControl(vendorIndex, answerIndex, 'questionText').value;
  }

  setAnswerQuestionText(vendorIndex: number, answerIndex: number, value: string): void {
    const control = this.getAnswerControl(vendorIndex, answerIndex, 'questionText');
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  getAnswerAnswerText(vendorIndex: number, answerIndex: number): any {
    return this.getAnswerControl(vendorIndex, answerIndex, 'answerText').value;
  }

  setAnswerAnswerText(vendorIndex: number, answerIndex: number, value: string): void {
    const control = this.getAnswerControl(vendorIndex, answerIndex, 'answerText');
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  // --- Photo related methods ---
  getPhotosFormArray(vendorIndex: number): FormArray<PhotoFormGroup> {
    const vendorGroup = this.vendorsFormArray.at(vendorIndex);
    return vendorGroup?.get('photos') as FormArray<PhotoFormGroup> ?? this.fb.array<PhotoFormGroup>([]);
  }

  getPhotoControl(vendorIndex: number, photoIndex: number, controlName: keyof PhotoFormValues): FormControl<any> {
    const photosArray = this.getPhotosFormArray(vendorIndex);
    if (!photosArray) {
      // console.warn(`Photos FormArray not found for vendorIndex: ${vendorIndex}`);
      return new FormControl();
    }
    const photoGroup = photosArray.at(photoIndex);
    if (!photoGroup) {
      // console.warn(`Photo FormGroup not found for vendorIndex: ${vendorIndex}, photoIndex: ${photoIndex}`);
      return new FormControl();
    }
    return photoGroup.get(controlName) as FormControl<any>;
  }

  getPhotoSelected(vendorIndex: number, photoIndex: number): boolean {
    return this.getPhotoControl(vendorIndex, photoIndex, 'selected').value;
  }

  setPhotoSelected(vendorIndex: number, photoIndex: number, value: boolean): void {
    const control = this.getPhotoControl(vendorIndex, photoIndex, 'selected');
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  getPhotoFileUrl(vendorIndex: number, photoIndex: number): string {
    return this.getPhotoControl(vendorIndex, photoIndex, 'fileUrl').value;
  }

  getPhotoFileName(vendorIndex: number, photoIndex: number): string {
    return this.getPhotoControl(vendorIndex, photoIndex, 'fileName').value;
  }

  getPhotoBase64Content(vendorIndex: number, photoIndex: number): string | null {
    return this.getPhotoControl(vendorIndex, photoIndex, 'base64Content').value;
  }

  async onFileSelected(event: any, vendorIndex: number): Promise<void> {
    const file: File = event.target.files[0];
    if (file) {
      const photosFormArray = this.getPhotosFormArray(vendorIndex);
      const reader = new FileReader();

      reader.onload = async (e: any) => {
        const base64String = e.target.result as string;
        // For custom uploads, fileUrl is intentionally empty as it's base64
        photosFormArray.push(this.createPhotoFormGroup('', file.name, null, base64String));
        this.message.success('Photo added successfully!');
      };

      reader.onerror = (error) => {
        // console.error('Error reading file:', error);
        this.message.error('Failed to read file. Please try again.');
      };

      reader.readAsDataURL(file);
    }
  }

  removePhoto(vendorIndex: number, photoIndex: number): void {
    const photosFormArray = this.getPhotosFormArray(vendorIndex);
    const photoControl = photosFormArray.at(photoIndex) as PhotoFormGroup;
    // Only allow removal of custom photos (where originalFileReferenceId is null AND fileUrl is empty because it's base64)
    if (photoControl.get('originalFileReferenceId')?.value === null && photoControl.get('fileUrl')?.value === '') {
      photosFormArray.removeAt(photoIndex);
      this.message.warning('Custom photo removed.');
    } else {
      this.message.error('Only custom photos can be removed. Uncheck to exclude existing photos.');
    }
  }

  onDownloadPpt(): void {
    this.isLoading = true;
    this.message.info('Generating PPT... This might take a moment.');
    // console.log('Downloading PPT with data:', this.reviewData);

    if (this.reviewData.eventDetails && this.reviewData.vendorSubmissions) {
      this.clientPptGenService.generateVendorSubmissionClientPPT(
        this.reviewData.eventDetails,
        this.reviewData.vendorSubmissions
      ).then(() => {
        this.isLoading = false;
        this.message.success('PPT generated and downloaded successfully!');
        // this.modalRef.destroy();
      }).catch(error => {
        this.isLoading = false;
        this.message.error('Failed to generate PPT. Please try again.');
        console.error('PPT Generation Error:', error);
      });
    } else {
      this.isLoading = false;
      this.message.error('Missing event details or vendor submissions to generate PPT.');
    }
  }

  getSelectedPhotoCount(vendorIndex: number): number {
    const photosArray = this.getPhotosFormArray(vendorIndex);
    return photosArray.controls.filter(ctrl => ctrl.get('selected')?.value === true).length;
  }

  hasPhotoLimitExceeded(): boolean {
    return this.vendorsFormArray.controls.some((_, i) => this.getSelectedPhotoCount(i) > 4);
  }

  addCustomAnswer(vendorIndex: number): void {
    const answersFormArray = this.getAnswersFormArray(vendorIndex);
    answersFormArray.push(this.createAnswerFormGroup('', '', null, null));
    this.message.info('New custom question added.');
  }

  removeCustomAnswer(vendorIndex: number, answerIndex: number): void {
    const answersFormArray = this.getAnswersFormArray(vendorIndex);
    const answerControl = answersFormArray.at(answerIndex) as AnswerFormGroup;
    if (answerControl.get('originalAnswerId')?.value === null) {
      answersFormArray.removeAt(answerIndex);
      this.message.warning('Custom question removed.');
    } else {
      this.message.error('Only custom questions can be removed.');
    }
  }
}